import * as Actions from '../actions/eventActions';
import Event from '../../entities/event';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type EventAction = { 
    type: string; 
    event: Event | null; 
    eventId: string | Event; 
    partial: boolean; 
    lastFetch: number; 
    events: Event[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Event> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Event[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Event> | null, firmId: string, userId: string, updates?: Event[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'event', 'events', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Event>} data
 * @param {Event[]} [updates]
 * @returns {ArrayMap<Event>}
 */
function updateStorage(data: ArrayMap<Event>, updates?: Event[]): ArrayMap<Event> {
	if (logging) console.debug('⮂ eventReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Event[]} [updates]
 * @returns {ArrayMap<Event>}
 */
function updateStorageNoReturn(updates?: Event[]): void {
	if (logging) console.debug('⮂ eventReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Event[]} [data]
 * @returns {ArrayMap<Event>}
 */
function createState(data?: Event[] | ArrayMap<Event>): ArrayMap<Event> {
	if (!data) return ArrayMapRegistry.clone<Event>('event', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Event>('event', data.map(e => e));
    else return ArrayMapRegistry.clone<Event>('event', data);
}

function events(state: ArrayMap<Event>, action: EventAction): ArrayMap<Event> {
	if (!state) state = createState();

	let st: ArrayMap<Event>;

	switch (action.type) {
		// action.event - the new value of event with id action.id
		// action.eventId - id of the target, used if new value is null
		case Actions.EVENT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ eventReducer: FETCH_ONE_SUCCESS', action);

			if (action.event === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.eventId)) {
				st = state.clone();
				st.set(action.event);
				// state.clear();

				updateStorageNoReturn([action.event]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.events
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.events
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.events
			 !partial, !stateEmpty, fetchEmpty	-> D return events in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge events in state newer than those in action.events
		 */
		case Actions.EVENT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.events.length === 0;
			const fetched = action.events.map(event => new Event({...event, persisted: true}));

			if (logging) console.debug(`⮂ eventReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} events with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ eventReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ eventReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Event> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Event[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(event => {
					const current = st.get(event.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && event.status === 'D') {
							// Remove it from state
							toUpdate.push(event);
							st.delete(event);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (event.addedOn > current.addedOn) {
								toUpdate.push(event);
								st.set(event);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (event.status !== 'D') {
							toUpdate.push(event);
							st.set(event);
						}
						else if (logging) console.debug('⮂ eventReducer: partialMerge: skipping deleted event not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ eventReducer: empty: events', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ eventReducer: fullMerge: events', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localEvent => {
                    // If this event has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localEvent.persisted) {
                        const fetchedEvent = st.get(localEvent.id);
                        if (!fetchedEvent || localEvent.addedOn > fetchedEvent.addedOn) st.set(localEvent);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.EVENT_FETCH_ONE_FAILURE:
			console.error('EVENT_FETCH_ONE_FAILURE', action.eventId, action.err);

			// No update to storage required
			return state;

		case Actions.EVENT_FETCH_FAILURE:
			console.error('EVENT_FETCH_FAILURE', action.eventId, action.err);

			// No update to storage required
			return state;

		case Actions.EVENT_RESTORE: {
		    let event = new Event({...action.event});
            st = state.clone();

            const existing = st.get(event.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (event.addedOn >= existing.addedOn) {
                    st.set(event);
                    return updateStorage(st, [event]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (event.status === 'D') return state;

                st.set(event);
                return updateStorage(st, [event]);
            }
        }
            
		case Actions.EVENT_SAVE: {
		    // console.debug('⮂ EVENT_SAVE reducer', action.event?.id);
		    
			// clone the event
			let event = new Event({...action.event});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new event in the state
			// If this is a create, add to the queue
			const _event = st.get(event.id);

			if (!_event) st.set(event);
			else {
				if (event.addedOn >= _event.addedOn) st.set(event);
			}

			return updateStorage(st, [event]);
		}

		case Actions.EVENT_SAVE_MANY: {
		    if (logging) console.debug('⮂ EVENT_SAVE_MANY reducer', action.events.map(event => event.id).join(','));
		    
			// clone the events
			let toProcess = action.events.map(event => new Event(event));

			st = state.clone();
			// state.clear();

			const updates: Event[] = [];

			toProcess.forEach(event => {
				// Add or update the new event in the state
				// If this is a create, add to the queue
				const existing = st.get(event.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (event.status !== 'D') {
						st.push(event);
						updates.push(event);
					}
					else if (logging) console.debug('⮂ EVENT_SAVE_MANY: skipping deleted event not in state');
				}
				else {
					if (event.addedOn >= existing.addedOn) {
						st.set(event);
						updates.push(event);
					}
					else if (logging) console.debug('⮂ Skipping, existing event had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.EVENT_REMOVE: {
			// clone the event
			const event = new Event({...action.event});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(event);

			return updateStorage(st, [event]);
		}

		case Actions.EVENT_RESTORE_FAILURE:
		case Actions.EVENT_SAVE_FAILURE: {
		    console.warn('EVENT_SAVE_FAILURE reducer', action.event?.id);
		    if (!action.event?.id) {
				console.warn('EVENT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const event = ensure(Event, action.event);
			if (!event) {
				console.error('Could not find event after call to save');
				return state;
			}

			st = state.clone();

			const _eventRef = state.get(event.id);

			if (_eventRef && (_eventRef.addedOn === event.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _event = new Event({..._eventRef});
				_event.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_event.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('event', _event).then(r => {
						if (r.isErr()) console.error(`⮂ eventReducer: failed to save event with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ eventReducer: saved event with fatalError info');
					}).catch(err => console.error(`⮂ eventReducer: failed to save event with fatalError info: ${err.message}`));

				st.set(_event);
			}

			return updateStorage(st);
		}

		case Actions.EVENT_SAVE_MANY_FAILURE: {
		    console.warn('EVENT_SAVE_MANY_FAILURE reducer', action.events.map(event => event.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.EVENT_RESTORE_SUCCESS:
		case Actions.EVENT_SAVE_SUCCESS: {
			if (!action.event?.id) {
				console.warn('EVENT_SAVE_SUCCESS reducer');
                return state;
			}

			const eventRef = state.get(action.event?.id);
			if (!eventRef) {
				console.warn('Could not find event after call to save');
                return state;
			}

			st = state.clone();

			if (action.event.addedOn >= eventRef.addedOn) {
				st.set(action.event);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const event = new Event({...eventRef, persisted: true});
				st.set(event);
			}

			return updateStorage(st, [action.event]);
		}

		case Actions.EVENT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ EVENT_SAVE_MANY_SUCCESS reducer', action.events.map(event => event.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const events: Event[] = [];

			action.events.forEach(e => {
				const eventRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!eventRef) console.warn('Could not find event after call to save');
				else if (e.addedOn >= eventRef.addedOn) {
				    st.set(e);
				    events.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const event = new Event({...eventRef, persisted: true});
					st.set(event);
					events.push(event);
				}
			});

			return updateStorage(st, events);
		}

		case Actions.EVENT_REMOVE_FAILURE: {
		    console.warn('EVENT_REMOVE_FAILURE action', action.event?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.EVENT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.EVENT_RESTORE_MANY: {
			if (logging) console.debug('⮂ EVENT_RESTORE_MANY reducer', action.events.map(event => event.id).join(','));

			const toProcess = action.events.map(event => new Event({...event}));
			st = state.clone();

			const updates: Event[] = [];

			toProcess.forEach(event => {
				const existing = st.get(event.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (event.addedOn >= existing.addedOn) {
						st.set(event);
						updates.push(event);
					}
					else if (logging) console.debug('⮂ EVENT_RESTORE_MANY: skipping older version of event');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (event.status !== 'D') {
						st.set(event);
						updates.push(event);
					}
					else if (logging) console.debug('⮂ EVENT_RESTORE_MANY: skipping deleted event not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.EVENT_REMOVE_MANY: {
			if (logging) console.debug('⮂ EVENT_REMOVE_MANY reducer', action.events.map(event => event.id).join(','));

			const toProcess = action.events.map(event => new Event({...event}));
			st = state.clone();

			toProcess.forEach(event => {
				st.delete(event);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default events;